import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Tag } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../Helper/NotificationProvider';
import { addMatching } from '../../../slices/matchingSlice';
import Editor from '../../Locations/Editor/Editor';
import MatchingToolbar from './Toolbar';
import { useLocation } from 'react-router-dom';
import { PauseCircleOutlined, ScanOutlined, UndoOutlined } from '@ant-design/icons';
import { URL_EVENTS, URL_LOCATIONS } from '../../../service/endpoints';
import ChairDetails from './ChairDetails/ChairDetails';
import FileUpload from './FileUpload/FileUpload';
import FileExport from './FileExport/FileExport';
import { getData, patchData } from '../../../service/APIClient';
import ResetButton from './ResetButton/ResetButton';
import { fetchTemplates } from '../../../slices/templatesSlice';
import DebouncedInput from './DebouncedInput/DebouncedInput';
import { fetchEvents, patchEvent } from '../../../slices/eventSlice';
import dayjs from 'dayjs';
import MultipleResetButton from './ResetButton/MultipleResetButton';


const Matchings = () => {
  const labelsToAdd = useRef([]);
  const editorRef = useRef();
  const dispatch = useDispatch();
  const locationNavigate = useLocation();
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const [labelId, setLabelId] = useState('');
  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [ currentLocation, setCurrentLocation ] = useState({});
  const [ currentScanPathId, setCurrentScanPathId] = useState(null)
  const { id } = locationNavigate.state;
  const [scanningMode, setScanningMode] = useState(false)
  const [patcheData, setPatchedData] = useState(false);
  const [open, setOpen] = useState(false);
  const [guest, setGuest] = useState({});
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [scanPathCopy, setScanPathCopy] = useState(null);
  const [scanPathsCopy, setScanPathsCopy] = useState([]);
  const [layoutCopy, setLayoutCopy] = useState([])
  const [event, setEvent] = useState(null)
  const [guestListCopy, setGuestListCopy] = useState(null)
  const debouncedInputRef = useRef();

  const fetchEvent = async () => {
    const response = await getData(`${URL_EVENTS}/${id}`)
    const data = {
      key: response?._id?.toString(),
      name: response.name,
      location: response?.location,
      startTime: dayjs(response?.startTime).toISOString(),
      endTime: dayjs(response?.endTime).toISOString(),
      description: response?.description,
      templateId: response?.templateId,
      templateName: response?.templateName,
      guestList: response?.guestList,
      status: response?.status
    }
    setEvent(data)
    setGuestListCopy(data?.guestList)
  }

  useEffect(() => {
    fetchEvent()
  }, []);

  useEffect(() => {
    dispatch(fetchTemplates());
  }, []);

  useEffect(() => {
    setScanPathsCopy(currentLocation.scanPaths)
    setLayoutCopy(currentLocation.layout)
  }, [currentLocation]);

  useEffect(() => {
    setCurrentScanPathId(null)
    setSelectedMarker(null)
    setScanningMode(false)
    setLabelId("")
  }, [patcheData])

  // Auto-focus input when scanning mode activates
  useEffect(() => {
    if (scanningMode) {
      setTimeout(() => debouncedInputRef.current?.setLabelIdFromParent(''), 50);
    }
  }, [scanningMode]);

  const KNOWN_KEYS = [
    'UID',
    'Sitzplatznummer',
    'Reihe',
    'Block',
    'Template',
    'Label-ID',
    'Vorname',
    'Nachname',
    'Anrede/Titel',
    'Firma',
    'Freifeld',
  ];

  const onClose = async (newChair = null, shape = null) => {
    setOpen(false);
    if (newChair) {

      const updatedGuestList = event.guestList.map(guest =>
        guest.chair === newChair.chair
          ? {
              ...guest,
              ...newChair,
            }
        : guest
      );
      setGuestListCopy(updatedGuestList)

      const customFieldsForItem = Object.entries(newChair?.matching?.customFields)
        .filter(([key]) => !KNOWN_KEYS.includes(key))
        .map(([key, value]) => ({
          zoneId: key,
          value,
        }));

      await dispatch(
        addMatching({
          labels: [
            {
              objectId: newChair?.matching?.label,
              data: [
                {
                  zoneId: 'template',
                  value: newChair?.matching?.template || event?.templateName,
                },
                {
                  zoneId: 'chair',
                  value: newChair.chair
                },
                {
                  zoneId: 'number',
                  value: shape?.shape.number
                },
                {
                  zoneId: 'name',
                  value: newChair.name
                },
                {
                  zoneId: 'surname',
                  value: newChair.surname
                },
                {
                  zoneId: 'title',
                  value: newChair.title
                },
                {
                  zoneId: 'company',
                  value: newChair.companyInfo
                },
                {
                  zoneId: 'message',
                  value: newChair.message
                },
                ...customFieldsForItem
              ],
            },
          ]
        })
      )

      await dispatch(patchEvent({
        eventId: event.key,
        changes: {
          guestList: updatedGuestList
        }
      }))
      fetchEvent()
      setPatchedData(!patcheData);
    }
  };

  const fetchData = async () => {
    try {
      const response = await getData(`${URL_LOCATIONS}/${event.location}`, {});
      setCurrentLocation(response)
    } catch (error) {
      console.error(error);
    }
  };

  const patchLocationData = async (updateData) => {
    try {
      await patchData(`${URL_LOCATIONS}/${event.location}`, updateData)
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if(event){
      fetchData();
      dispatch(fetchEvents)
    }
  },[event])

  const handleMove = async (direction) => {
    try {
      const scanPathIndex = scanPathsCopy.findIndex(
        (path) => path.id === currentScanPathId
      );

      if (scanPathIndex === -1) {
        openNotification(
          'bottomRight',
          t('Fehler'),
          t('Der aktuelle Scan-Pfad wurde nicht gefunden.'),
          false
        );
        return;
      }

      const currentScanPath = scanPathsCopy[scanPathIndex];
      const currentMarker = currentScanPath.currentMatchingMarker;
      const markerIndex = currentScanPath.path.findIndex(
        (id) => id === currentMarker
      );

      if (markerIndex === -1) {
        openNotification(
          'bottomRight',
          t('Fehler'),
          t('Der aktuelle übereinstimmende Stuhl wurde im Pfad nicht gefunden.'),
          false
        );
        return;
      }

      const newMarker = direction === "back" ?
        getPreviousMarker(markerIndex - 1, currentScanPath) :
        getNextMarker(markerIndex, scanPathCopy)

      if (newMarker) {
        setSelectedShapeId(newMarker);
        setSelectedMarker(newMarker);
        const updatedScanPaths = [...scanPathsCopy];
        updatedScanPaths[scanPathIndex] = {
          ...updatedScanPaths[scanPathIndex],
          currentMatchingMarker: newMarker,
        };
        setScanPathsCopy(updatedScanPaths)
        setLabelId('');
        setTimeout(() => debouncedInputRef.current?.setLabelIdFromParent(''), 20);
      } else {
        direction === "back" ?
        openNotification(
          'bottomRight',
          t('Fehler'),
          t('Sie befinden sich bereits am Anfang des Scan-Pfads.'),
          false
        ) :
          openNotification(
            'bottomRight',
            t('Abgeschlossen'),
            t('Sie befinden sich beim letzten Stuhl.'),
            true
          );
      }
    } catch (err) {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Ein Fehler beim Wechseln des Stuhls ist aufgetreten.'),
        false
      );
      console.error(err);
    }
  };


  const handleShapeSelect = async (shapeId) => {
    if (!shapeId) {
      // Clicked empty space — deselect everything silently
      setCurrentScanPathId(null);
      setSelectedShapeId(null);
      setSelectedMarker(null);
      setScanPathCopy(null);
      setGuest({});
      return;
    }
    const scanPath = scanPathsCopy?.find(path => path.path.includes(shapeId));
    if (!scanPath) {
      // Shape exists but has no scan path — silently ignore
      return;
    }
    setCurrentScanPathId(scanPath.id)
    setSelectedShapeId(shapeId)
    setSelectedChair(shapeId)

    //Initial Click or scanPath change — default to first non-special chair if no marker saved yet
    if(!selectedMarker || scanPath.id !== scanPathCopy?.id){
      const initialMarker = scanPath.currentMatchingMarker ??
        scanPath.path.find(pathId => {
          const c = layoutCopy?.find(c => c.chairId === pathId);
          return !c?.shape?.special;
        }) ??
        scanPath.path[0];
      setSelectedMarker(initialMarker);
    }

    //During scanning set new currentMatchingMarker in array of scanPaths
    if(scanningMode && shapeId !== selectedMarker){
      setSelectedMarker(shapeId)

      const scanPathIndex = scanPathsCopy.findIndex((path) =>
        path.path.includes(shapeId)
      );
      const updatedScanPaths = [...scanPathsCopy];
      updatedScanPaths[scanPathIndex] = {
        ...updatedScanPaths[scanPathIndex],
        currentMatchingMarker: shapeId,
      };
      setScanPathsCopy(updatedScanPaths)
    }

    setScanPathCopy(scanPath)
  };

  const handleShapeSelectCb = useCallback(id => handleShapeSelect(id), [handleShapeSelect]);

  const setSelectedChair = (shapeId) => {
    const chair = guestListCopy.find(
      chair => chair.chair === shapeId
    );
    setGuest(chair)
  }

  const handleStartScanning = async () => {
    if (!selectedShapeId) {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Bitte wählen Sie eine Form aus, um mit dem Scannen zu beginnen.'),
        false
      );
      return;
    }
    await fetchData()
    const scanPath = scanPathsCopy.find(path => path.path.includes(selectedShapeId));

    if (!scanPath) {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Der ausgewählte Stuhl gehört zu keinem Scan-Pfad'),
        false
      );
      return;
    }

    openNotification(
      'bottomRight',
      t('Start'),
      t('Das Scannen für den ausgewählten Scan-Pfad wurde gestartet.'),
      true
    );

    setScanningMode(true);
  };  // auto-focus is handled by the useEffect below


  const mergeGuestList = (latestData, localData) => {
    return latestData.map(latestChair => {
      const localChair = localData.find(chair => chair.chair === latestChair.chair);

      if (localChair) {
        return {
          ...latestChair,
          matching: {
            ...latestChair.matching, // Keep latest matching
            ...(localChair.matching.matched ? localChair.matching : {}) // Apply local updates only if matched
          }
        };
      }
      return latestChair; // Keep as is if not found in local updates
    });
  };

  const syncData = async () => {
    const response = await getData(`${URL_EVENTS}/${event.key}`, {})
    return mergeGuestList(response.guestList, guestListCopy);
  }

  const handleStopScanning = async () => {
    try {
        const mergedGuestList = await syncData();
        console.log(mergedGuestList)
        setGuestListCopy(mergedGuestList)
        if(labelsToAdd.current.length > 0)
           await dispatch(addMatching({ labels: labelsToAdd.current }));
        labelsToAdd.current = [];
        const updateData = { scanPaths: scanPathsCopy };
        await patchLocationData(updateData);
        await dispatch(patchEvent({
          eventId: event.key,
          changes: {
            guestList: mergedGuestList
          }
        }))
        fetchEvent()
        setPatchedData(!patcheData);
        openNotification(
          'bottomRight',
          t('Erfolg'),
          t('Daten wurden gespeichert.'),
          true
        );
    } catch (err) {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Fehler beim Speichern der Daten aufgetreten. Bitte versuchen Sie es erneut'),
        false
      );
    }
  };

  const updateChairMatching = async (id) => {
    try {
      const scanPath = scanPathsCopy.find(
        (path) => path.id === currentScanPathId
      );

      if (!currentScanPathId || !scanPath) {
        openNotification(
          'bottomRight',
          t('Fehler'),
          t('Der aktuelle Scan-Pfad wurde nicht gefunden.'),
          false
        );
        return;
      }

      const shapeId = scanPath.currentMatchingMarker

      const guestIndex = guestListCopy.findIndex(
        (chair) => chair.chair === shapeId
      )

      const chairIndex = layoutCopy.findIndex(
        (chair) => chair.chairId === shapeId
      )

      const guest = guestListCopy[guestIndex];
      const chair = layoutCopy[chairIndex];

      if (guest.matching.label === id) {
        openNotification(
          'bottomRight',
          t('Fehler'),
          t('Dieses Label wurde bereits dem ausgewählten Stuhl zugewiesen.'),
          false
        );
        return;
      }

      const updatedGuest = {
        ...guest,
        matching: {
            matched: true,
            label: id,
            template: '',
            customFields: {}
          }
      };

      const updatedGuestList = [
          ...guestListCopy.slice(0, guestIndex),
          updatedGuest,
          ...guestListCopy.slice(guestIndex + 1),
        ]

      setGuestListCopy(updatedGuestList)
      labelsToAdd.current.push(
        {
          objectId: id,
          data: [
            {
              zoneId: 'template',
              value: event?.templateName,
            },
            {
              zoneId: 'chair',
              value: updatedGuest.chair
            },
            {
              zoneId: 'number',
              value: chair.shape.number
            },
            {
              zoneId: 'row',
              value: chair.shape.row
            },
            {
              zoneId: 'block',
              value: chair.shape.block
            },
            {
              zoneId: 'name',
              value: updatedGuest.name
            },
            {
              zoneId: 'surname',
              value: updatedGuest.surname
            },
            {
              zoneId: 'title',
              value: updatedGuest.title
            },
            {
              zoneId: 'company',
              value: updatedGuest.companyInfo
            },
            {
              zoneId: 'message',
              value: updatedGuest.message
            }
          ],
        }
      )
    } catch (err) {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Ein Fehler ist beim Aktualisieren der Stuhlzuweisung aufgetreten.'),
        false
      );
      console.error(err);
    }
  }

  function getNextMarker(markerIndex, currentScanPath) {

    if (markerIndex >= currentScanPath.path.length) {
      console.warn("Keine weiteren Stühle im Scan-Pfad vorhanden.");
      return null;
    }

    const nextMarker = currentScanPath.path[markerIndex + 1];

    const chairIndex = layoutCopy.findIndex(
      (chair) => chair.chairId === nextMarker
    );

    if (chairIndex === -1) {
      console.warn(`Stuhl mit der ID ${nextMarker} nicht gefunden. Wird übersprungen.`);
      return null
    }

    const chair = layoutCopy[chairIndex];
    if (chair?.shape?.special === true) {
      return getNextMarker(markerIndex + 1, currentScanPath);
    }

    return nextMarker;
  }

  function getPreviousMarker(markerIndex, currentScanPath) {
    if (markerIndex < 0) {
      console.warn("Bereits am Anfang des Scan-Pfads.");
      return null;
    }

    const previousMarker = currentScanPath.path[markerIndex];

    const chairIndex = layoutCopy.findIndex(
      (chair) => chair.chairId === previousMarker
    );

    if (chairIndex === -1) {
      console.warn(`Stuhl mit der ID ${previousMarker} nicht gefunden. Wird übersprungen.`);
      return getPreviousMarker(markerIndex - 1, currentScanPath);
    }

    const chair = layoutCopy[chairIndex];
    if (chair?.shape?.special === true) {
      return getPreviousMarker(markerIndex - 1, currentScanPath);
    }

    return previousMarker;
  }

  const labelAlreadyScanned = (labelId) => {
    return guestListCopy.find((chair) => chair?.matching?.label === labelId);
  }

  const handleSubmit = async (value) => {
    const id = value ?? labelId
    if (id.length !== 8 || !/^[0-9a-fA-F]{8}$/.test(id)) {
      openNotification('bottomRight', 'Fehler', 'Bitte geben Sie einen gültigen 8-stelligen Hexadezimalwert ein.', false);
      return;
    }

    try {
      if(labelAlreadyScanned(id)){
        setLabelId("")
        openNotification('bottomRight', 'Fehler', 'Label wird in diesem Layout bereits bei einem anderen Stuhl verwendet.', false);
        return;
      }
      await updateChairMatching(value);
      const scanPathIndex = scanPathsCopy.findIndex(
        (path) => path.id === currentScanPathId
      );

      if (scanPathIndex === -1) {
        openNotification(
          'bottomRight',
          t('Fehler'),
          t('Der aktuelle Scan-Pfad wurde nicht gefunden.'),
          false
        );
        return;
      }

      const currentScanPath = scanPathsCopy[scanPathIndex];
      const currentMarker = currentScanPath.currentMatchingMarker;
      const markerIndex = currentScanPath.path.findIndex(
        (id) => id === currentMarker
      );
      const nextMarker = getNextMarker(markerIndex, currentScanPath)

      if (nextMarker) {
        setSelectedShapeId(nextMarker);
        setSelectedMarker(nextMarker);
        const updatedScanPaths = [...scanPathsCopy];
        updatedScanPaths[scanPathIndex] = {
          ...updatedScanPaths[scanPathIndex],
          currentMatchingMarker: nextMarker,
        };
        setScanPathsCopy(updatedScanPaths)
      } else {
          openNotification(
            'bottomRight',
            t('Abgeschlossen'),
            t('Sie befinden sich beim letzten Stuhl.'),
            true
          );
      }
      setLabelId('');
    } catch (err) {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Ein Fehler ist beim Übermitteln Ihrer Daten aufgetreten.'),
        false
      );
      console.error(err);
    }
  };


  const editorNode = useMemo(() => (
    <Editor
      ref={editorRef}
      key={patcheData}
      currentScanPathId={currentScanPathId}
      viewOnly={true}
      locationId={event?.location}
      selectedShapeId={selectedShapeId}
      onShapeSelect={handleShapeSelectCb}
      setOpen={setOpen}
      patcheData={patcheData}
      setPatchedData={setPatchedData}
      setLocation={setCurrentLocation}
      selectedMarker={selectedMarker}
      guestList={guestListCopy}
      fetchEvent={fetchEvent}
      event={event}
    />
  ), [
    currentScanPathId,
    selectedShapeId,
    handleShapeSelectCb,
    setOpen,
    patcheData,
    setPatchedData,
    setCurrentLocation,
    selectedMarker,
    layoutCopy,
    guestListCopy
  ]);


  return event && (
    <div
      style={{
        marginTop: '17px',
        marginLeft: '15px',
        marginRight: '15px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          rowGap: 8,
          marginBottom: 10,
          marginLeft: '55px'
        }}
      >
        {scanningMode ?
          <Button
            type="default"
            style={{ marginRight: '10px' }}
            onClick={async () => {
              setScanningMode(false)
              await handleStopScanning()
            }}
            icon={<PauseCircleOutlined style={{color: "red" }}/>}
          >
            {t('Stoppe Scannen')}
          </Button>
          :
          <Button
            type="default"
            style={{ marginRight: '10px' }}
            onClick={handleStartScanning}
            icon={<ScanOutlined/>}
          >
            {t('Starte Scannen')}
          </Button>
        }
        <DebouncedInput
          ref={debouncedInputRef}
          disabled={!scanningMode}
          placeholder={t('Label ID')}
          maxLength={8}
          setLabelId={setLabelId}
          pause={500}  // or whatever delay you like
          onFinished={label => {
            // this only fires *once* per “typing pause”
            handleSubmit(label);
          }}
        />
        <MatchingToolbar
          // submit={() => {
          //   debouncedInputRef.current.setLabelIdFromParent('')
          //   handleSubmit()
          //   }
          // }
          skip={() => handleMove("next")}
          goback={() => handleMove("back")}
          scanningMode={scanningMode}
        />
        <Tag color="green" style={{marginLeft: 5}}>Bereits gematched</Tag>
        <Tag color="red">Nicht gematched</Tag>
        <ResetButton
          patcheData={patcheData}
          setPatchedData={setPatchedData}
          location={currentLocation}
          guestList={guestListCopy}
          event={event}
          fetchEvent={fetchEvent}
          template={"Default"}
          marginLeft={10}
        />
        <MultipleResetButton
          editorRef={editorRef}
          patcheData={patcheData}
          setPatchedData={setPatchedData}
          location={currentLocation}
          guestList={guestListCopy}
          event={event}
          fetchEvent={fetchEvent}
          template={"Default"}
          marginLeft={10}
        />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection:"row",
          flexWrap: 'wrap',
          rowGap: 8,
          marginBottom: 10,
          marginLeft: '55px'
        }}
      >
        <FileUpload
          fetchEvent={fetchEvent}
          currentLocation={currentLocation}
          guestList={guestListCopy}
          event={event}
          fetchData={fetchData}
          patchData={patchLocationData}
          setPatchedData={setPatchedData}
          patcheData={patcheData}
        />
        <FileExport
          guestList={guestListCopy}
          currentLocation={currentLocation}
          event={event}
        />
        <Button
          icon={<UndoOutlined />}
          onClick={async () => {
            await fetchEvent()
            setPatchedData(!patcheData)
          }}
          style={{ marginLeft: '10px' }}
        >
          Daten aktualisieren
        </Button>

      </div>
      {editorNode}
      {layoutCopy && <ChairDetails
        open={open}
        onClose={onClose}
        selectedShapeId={selectedShapeId}
        layoutCopy={layoutCopy}
        guest={guest}
        event={event}
      />}
    </div>
  );
};

export default Matchings;
